'use client';

import Link from 'next/link';
import { motion } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';
import { PROJECTS } from '@data/portfolioData';

const BENTO_LAYOUTS = {
    1: [
        'md:col-start-1 md:col-span-3 md:row-start-1',
    ],
    2: [
        'md:col-start-1 md:col-span-2 md:row-start-1',
        'md:col-start-3 md:row-start-1',
    ],
    3: [
        'md:col-start-1 md:row-start-1',
        'md:col-start-2 md:row-start-1',
        'md:col-start-3 md:row-start-1',
    ],
    4: [
        'md:col-start-1 md:row-start-1 md:row-span-2',
        'md:col-start-2 md:row-start-1',
        'md:col-start-3 md:row-start-1',
        'md:col-start-2 md:col-span-2 md:row-start-2',
    ],
    5: [
        'md:col-start-1 md:row-start-1',
        'md:col-start-2 md:row-start-1',
        'md:col-start-3 md:row-start-1',
        'md:col-start-1 md:col-span-2 md:row-start-2',
        'md:col-start-3 md:row-start-2',
    ],
    6: [
        'md:col-start-1 md:row-start-1 md:row-span-2',
        'md:col-start-2 md:row-start-1',
        'md:col-start-3 md:row-start-1',
        'md:col-start-2 md:col-span-2 md:row-start-2',
        'md:col-start-1 md:col-span-2 md:row-start-3',
        'md:col-start-3 md:row-start-3',
    ],
    7: [
        'md:col-start-1 md:row-start-1 md:row-span-2',
        'md:col-start-2 md:row-start-1',
        'md:col-start-3 md:row-start-1',
        'md:col-start-2 md:col-span-2 md:row-start-2',
        'md:col-start-1 md:row-start-3',
        'md:col-start-2 md:row-start-3',
        'md:col-start-3 md:row-start-3',
    ],
};

function PortfolioCard({ item, bentoClass, index }) {
    const hasBg = Boolean(item.image);
    const href = item.href || (item.slug ? `/portfolio/${item.slug}` : null);

    const inner = (
        <>
            <div
                className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-105 bg-cover bg-center"
                style={{ backgroundImage: hasBg ? `url(${item.image})` : item.gradient }}
            />

            <div
                className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
                style={{
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
                }}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10 group-hover:from-black/75 transition-all duration-400" />

            <div className="absolute inset-0 flex flex-col justify-between p-5 md:p-6">
                <div className="flex items-center gap-1.5 flex-wrap">
                    {item.category && (
                        <span className="px-2.5 py-1 rounded-md bg-white/15 backdrop-blur-sm text-white/90 text-[10px] font-bold uppercase tracking-wider border border-white/10">
                            {item.category}
                        </span>
                    )}
                    {item.industry && (
                        <span className="px-2.5 py-1 rounded-md bg-white/15 backdrop-blur-sm text-white/90 text-[10px] font-bold uppercase tracking-wider border border-white/10">
                            {item.industry}
                        </span>
                    )}
                </div>

                <div className="flex items-end justify-between gap-3">
                    <h3 className="text-white font-bold text-base md:text-lg leading-snug flex-1">
                        {item.title}
                    </h3>
                    <div className="relative shrink-0 w-8 h-8">
                        <span className="absolute -inset-2 rounded-full bg-primary/0 group-hover:bg-primary/25 group-hover:animate-pulse pointer-events-none transition-colors duration-300" />
                        <div className="relative w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-[0_4px_12px_rgba(224,39,74,0.35)] group-hover:shadow-[0_8px_20px_rgba(224,39,74,0.55)] transition-shadow duration-300">
                            <ArrowUpRight size={14} className="text-white" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.07 }}
            className={`group relative overflow-hidden rounded-2xl cursor-pointer min-h-[220px] ${bentoClass}`}
        >
            {href && (
                <Link href={href} className="absolute inset-0 z-10" aria-label={item.title} />
            )}
            {inner}
        </motion.div>
    );
}

export default function PortfolioService({ data, filterKey }) {
    if (!data) return null;

    const filtered = filterKey
        ? PROJECTS.filter(p => p.key === filterKey)
        : PROJECTS;

    const items = filtered.slice(0, 4);
    if (!items.length) return null;

    const count = items.length;
    const bentoClasses = BENTO_LAYOUTS[count] ?? BENTO_LAYOUTS[4];

    return (
        <section className="relative bg-white py-12 md:py-20 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-primary/[0.04] rounded-full blur-[120px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[300px] bg-primary/[0.03] rounded-full blur-[100px]" />
            </div>

            <div className="relative z-10 max-w-[1440px] mx-auto px-6">
                <motion.div
                    className="text-center mb-10"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                    <h2 className="text-4xl md:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight">
                        {data.titleText}{' '}
                        <span className="text-primary">{data.coloredText}</span>
                    </h2>
                    {data.description && (
                        <p className="mt-4 text-muted-foreground max-w-4xl mx-auto text-sm md:text-base">
                            {data.description}
                        </p>
                    )}
                </motion.div>

                <div
                    className="grid grid-cols-1 md:grid-cols-3 gap-3.5"
                    style={{ gridAutoRows: '240px' }}
                >
                    {items.map((item, i) => (
                        <PortfolioCard
                            key={item.id}
                            item={item}
                            bentoClass={bentoClasses[i] ?? ''}
                            index={i}
                        />
                    ))}
                </div>

                <div className="flex justify-center mt-5">
                    Explore all our portfolios
                    <Link
                        href="/portfolio"
                        className="group inline-flex items-center gap-1.5 text-primary font-semibold text-sm no-underline transition-colors duration-200 ml-1.5"
                    >
                        View All Projects
                        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>
            </div>
        </section>
    );
}
